import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import toast from 'react-hot-toast';

export default function Tournaments({ user, role }) {
    const [tournaments, setTournaments] = useState([]);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    
    // --- Form States ---
    const [tTitle, setTTitle] = useState('');
    const [tFormat, setTFormat] = useState('Arena');
    const [tTimeControl, setTTimeControl] = useState('3+0 Blitz');
    const [tLevel, setTLevel] = useState('All Levels');
    const [tDate, setTDate] = useState('');
    const [tLink, setTLink] = useState('');

    // --- Right-Click & Edit States ---
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, tournament: null });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    // Hide context menu on normal click
    useEffect(() => {
        const handleClick = () => setContextMenu({ ...contextMenu, visible: false });
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [contextMenu]);

    useEffect(() => {
        const unsub = onSnapshot(collection(db, "tournaments"), (snap) => {
            const list = [];
            snap.forEach(d => list.push({ id: d.id, ...d.data() }));
            setTournaments(list.sort((a,b) => new Date(a.date) - new Date(b.date)));
        });
        return () => unsub();
    }, []);

    const handleCreateTournament = async (e) => {
        e.preventDefault();
        const loadingId = toast.loading("Publishing Tournament...");
        try {
            await addDoc(collection(db, "tournaments"), {
                title: tTitle, format: tFormat, timeControl: tTimeControl, 
                level: tLevel, date: tDate, link: tLink, 
                isSynced: false,
                createdBy: user.email.toLowerCase(), createdAt: new Date().toISOString()
            });
            setTTitle(''); setTFormat('Arena'); setTTimeControl('3+0 Blitz'); 
            setTLevel('All Levels'); setTDate(''); setTLink(''); 
            setShowCreateForm(false);
            toast.success("Tournament Published to Academy! 🏆", { id: loadingId });
        } catch (error) {
            toast.error("Failed to create tournament.", { id: loadingId });
        }
    };

    // ==========================================
    // RIGHT-CLICK LOGIC (WITH BOUNDARY FIX)
    // ==========================================
    const handleContextMenu = (e, tournament) => {
        if (role !== 'OWNER' && role !== 'COACH') return; // Only Admins can right-click
        e.preventDefault();
        
        const menuWidth = 200;
        const menuHeight = 150;
        let clickX = e.clientX;
        let clickY = e.clientY;

        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;

        if (clickX + menuWidth > screenWidth) clickX = screenWidth - menuWidth - 15;
        if (clickY + menuHeight > screenHeight) clickY = screenHeight - menuHeight - 15;

        setContextMenu({
            visible: true,
            x: clickX,
            y: clickY,
            tournament: tournament
        });
    };

    // ==========================================
    // EDIT & DELETE LOGIC
    // ==========================================
    const openEditModal = (tournament) => {
        setEditData(tournament);
        setIsEditModalOpen(true);
    };

    const handleUpdateTournament = async (e) => {
        e.preventDefault();
        const loadingId = toast.loading("Updating Tournament...");
        try {
            await updateDoc(doc(db, "tournaments", editData.id), {
                title: editData.title,
                format: editData.format,
                timeControl: editData.timeControl,
                level: editData.level,
                date: editData.date,
                link: editData.link
            });
            toast.success("Tournament Updated Successfully!", { id: loadingId });
            setIsEditModalOpen(false);
        } catch (error) {
            toast.error("Failed to update tournament.", { id: loadingId });
        }
    };

    const handleDeleteTournament = async (tournament) => {
        if(window.confirm(`🚨 Are you sure you want to delete "${tournament.title}"?`)) {
            await deleteDoc(doc(db, "tournaments", tournament.id));
            toast.success("Tournament Deleted.");
        }
    };

    const copyTournamentLink = (link) => {
        if (!link) return toast.error("No Lichess link added yet!");
        navigator.clipboard.writeText(link);
        toast.success("Link Copied! Paste it in WhatsApp.");
    };

    // 🤖 LICHESS API AUTO-SYNC MAGIC
    const handleSyncResults = async (tournament) => {
        if (!tournament.link || !tournament.link.includes('lichess.org/tournament/')) {
            return toast.error("Invalid Lichess link. Cannot sync.");
        }

        setIsSyncing(true);
        const loadingToast = toast.loading("Connecting to Lichess API...");

        try {
            const urlParts = tournament.link.split('/');
            const tId = urlParts[urlParts.length - 1].split('?')[0]; 

            const response = await fetch(`https://lichess.org/api/tournament/${tId}/results?max=50`);
            if (!response.ok) throw new Error("Tournament not found or not finished yet.");
            
            const text = await response.text();
            if (!text) throw new Error("No players found.");

            const lines = text.split('\n').filter(line => line.trim() !== '');
            const lichessResults = lines.map(line => JSON.parse(line));
            
            const usersSnap = await getDocs(query(collection(db, "users"), where("role", "==", "STUDENT")));
            const academyUsers = [];
            usersSnap.forEach(doc => academyUsers.push({ id: doc.id, ...doc.data() }));

            let matchedStudentsCount = 0;

            for (let result of lichessResults) {
                const lichessName = result.username.toLowerCase();
                const rank = result.rank;
                
                const student = academyUsers.find(u => u.lichessUsername && u.lichessUsername.toLowerCase() === lichessName);
                
                if (student) {
                    matchedStudentsCount++;
                    let earnedXp = 50; 
                    if (rank === 1) earnedXp = 500;
                    else if (rank === 2) earnedXp = 300;
                    else if (rank === 3) earnedXp = 200;

                    const newTournamentXp = (student.tournamentXp || 0) + earnedXp;
                    const newTotalXp = (student.totalXp || 0) + earnedXp;

                    await updateDoc(doc(db, "users", student.id), {
                        tournamentXp: newTournamentXp,
                        totalXp: newTotalXp
                    });
                }
            }

            await updateDoc(doc(db, "tournaments", tournament.id), { isSynced: true });
            toast.success(`Sync Complete! Updated XP for ${matchedStudentsCount} students. 🏆`, { id: loadingToast });

        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to sync. Make sure tournament is finished.", { id: loadingToast });
        }
        setIsSyncing(false);
    };

    const formatUrl = (url) => url ? (url.startsWith("http") ? url : `https://${url}`) : "#";

    const upcomingTournaments = tournaments.filter(t => new Date(t.date) >= new Date());
    const pastTournaments = tournaments.filter(t => new Date(t.date) < new Date());

    return (
        <div style={{width: '100%', maxWidth: '1000px', margin: '0 auto', paddingBottom: '40px', position: 'relative' }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{color: '#f59e0b', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}><span style={{fontSize: '30px' }}>🎪</span> Academy Tournaments</h2>
                {(role === 'OWNER' || role === 'COACH') && ( <button onClick={() => setShowCreateForm(!showCreateForm)} className={showCreateForm ? "neu-btn" : "neu-btn-primary"} style={showCreateForm ? { color: '#ef4444'} : {}}>{showCreateForm ? '✖ Cancel' : '➕ Create Tournament'}</button> )}
            </div>

            {/* CREATE TOURNAMENT FORM */}
            {showCreateForm && (role === 'OWNER' || role === 'COACH') && (
                <div className="glass-panel" style={{padding: '30px', marginBottom: '40px', }}>
                    <h3 style={{margin: '0 0 20px 0', color: 'var(--text-primary)' }}>Configure Tournament Details</h3>
                    <form onSubmit={handleCreateTournament} style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                        <div><label style={labelStyle}>Tournament Title</label><input type="text" value={tTitle} onChange={e=>setTTitle(e.target.value)} required placeholder="e.g. Sunday Grand Prix" className="neu-input" /></div>
                        <div><label style={labelStyle}>Pairing Format</label><select value={tFormat} onChange={e=>setTFormat(e.target.value)} className="neu-input"><option style={{background: 'var(--bg-primary)'}}>Arena (Continuous)</option><option style={{background: 'var(--bg-primary)'}}>Swiss (Rounds)</option></select></div>
                        <div><label style={labelStyle}>Time Control</label><select value={tTimeControl} onChange={e=>setTTimeControl(e.target.value)} className="neu-input"><option style={{background: 'var(--bg-primary)'}}>1+0 Bullet</option><option style={{background: 'var(--bg-primary)'}}>3+0 Blitz</option><option style={{background: 'var(--bg-primary)'}}>5+0 Blitz</option><option style={{background: 'var(--bg-primary)'}}>10+0 Rapid</option><option style={{background: 'var(--bg-primary)'}}>15+10 Rapid</option></select></div>
                        <div><label style={labelStyle}>Target Level</label><select value={tLevel} onChange={e=>setTLevel(e.target.value)} className="neu-input"><option style={{background: 'var(--bg-primary)'}}>All Levels</option><option style={{background: 'var(--bg-primary)'}}>Level 1 (Beginner)</option><option style={{background: 'var(--bg-primary)'}}>Level 2</option><option style={{background: 'var(--bg-primary)'}}>Level 3</option><option style={{background: 'var(--bg-primary)'}}>Level 4</option><option style={{background: 'var(--bg-primary)'}}>Level 5</option><option style={{background: 'var(--bg-primary)'}}>Level 6 (Pro)</option></select></div>
                        <div><label style={labelStyle}>Date & Time</label><input type="datetime-local" value={tDate} onChange={e=>setTDate(e.target.value)} required className="neu-input" /></div>
                        <div><label style={labelStyle}>Lichess Tournament Link</label><input type="text" value={tLink} onChange={e=>setTLink(e.target.value)} placeholder="https://lichess.org/tournament/..." className="neu-input" /></div>
                        <div style={{gridColumn: '1 / -1' }}><button type="submit" className="neu-btn-primary" style={{width: '100%', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', boxShadow: '0 4px 15px rgba(245, 158, 11, 0.4)' }}>🚀 Publish Tournament</button></div>
                    </form>
                </div>
            )}

            {/* UPCOMING TOURNAMENTS */}
            <h3 style={{color: '#10b981', marginBottom: '15px' }}>⏳ Upcoming Battles {(role === 'OWNER' || role === 'COACH') && <span style={{fontSize: '12px', color: 'var(--text-secondary)'}}>(Right-click to manage)</span>}</h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                {upcomingTournaments.length === 0 ? <p style={{color: 'var(--text-secondary)' }}>No upcoming tournaments right now.</p> : upcomingTournaments.map(t => (
                    <div 
                        key={t.id} 
                        onContextMenu={(e) => handleContextMenu(e, t)} // 🖱️ RIGHT CLICK LISTENER
                        className="glass-panel"
                        style={{padding: '25px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', cursor: (role === 'OWNER' || role === 'COACH') ? 'context-menu' : 'default', }}
                    >
                        <div>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '15px' }}>
                                    <span style={badgeStyle}>{t.level}</span><span style={{...badgeStyle, background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.5)'}}>{t.format}</span>
                                </div>
                            </div>
                            <h3 style={{margin: '0 0 5px 0', color: 'var(--text-primary)', fontSize: '22px' }}>{t.title}</h3>
                            <div style={{color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '5px' }}>⏱️ Time: {t.timeControl}</div>
                            <div style={{color: '#f59e0b', fontSize: '14px', marginBottom: '25px', fontWeight: 'bold' }}>📅 {new Date(t.date).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                        {t.link ? ( <a href={formatUrl(t.link)} target="_blank" rel="noreferrer" className="neu-btn" style={{display: 'block', textAlign: 'center', textDecoration: 'none', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.5)' }}>🔗 Join Lichess Arena</a> ) : ( <div style={{textAlign: 'center', padding: '12px', background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)', borderRadius: '50px', fontWeight: 'bold' }}>Link pending</div> )}
                    </div>
                ))}
            </div>

            {/* PAST TOURNAMENTS */}
            <h3 style={{color: '#64748b', marginBottom: '15px', paddingTop: '20px' }}>📜 Past Tournaments</h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
                {pastTournaments.length === 0 ? <p style={{color: '#64748b' }}>No past records.</p> : pastTournaments.map(t => (
                    <div 
                        key={t.id} 
                        onContextMenu={(e) => handleContextMenu(e, t)} // 🖱️ RIGHT CLICK LISTENER
                        className="glass-panel"
                        style={{padding: '20px', opacity: 0.8, cursor: (role === 'OWNER' || role === 'COACH') ? 'context-menu' : 'default' }}
                    >
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <h4 style={{margin: 0, color: 'var(--text-primary)' }}>{t.title}</h4>
                        </div>
                        <div style={{color: '#64748b', fontSize: '13px', marginBottom: '15px' }}>Played: {new Date(t.date).toLocaleDateString()}</div>
                        
                        {(role === 'OWNER' || role === 'COACH') && (
                            t.isSynced ? (
                                <div style={{color: '#10b981', fontSize: '12px', fontWeight: 'bold' }}>✅ Results Synced to Leaderboard</div>
                            ) : (
                                <button disabled={isSyncing} onClick={() => handleSyncResults(t)} className="neu-btn" style={{width: '100%', fontSize: '12px' }}>
                                    {isSyncing ? '🔄 Syncing...' : '🔄 Pull Lichess Results'}
                                </button>
                            )
                        )}
                    </div>
                ))}
            </div>

            {/* 🖱️ CUSTOM RIGHT-CLICK CONTEXT MENU */}
            {contextMenu.visible && (
                <div style={{position: 'fixed',
                    top: contextMenu.y,
                    left: contextMenu.x,
                    background: 'var(--bg-primary)',
                    border: '1px solid rgba(163, 177, 198, 0.4)',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.8)',
                    padding: '8px 0',
                    zIndex: 9999,
                    minWidth: '180px'
                }}>
                    <div style={{padding: '8px 15px', color: 'var(--text-secondary)', fontSize: '12px', borderBottom: '1px solid var(--bg-primary)', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        Managing: <strong style={{color: 'var(--text-primary)'}}>{contextMenu.tournament?.title}</strong>
                    </div>
                    
                    <button onClick={() => { openEditModal(contextMenu.tournament); setContextMenu({ ...contextMenu, visible: false }); }} style={menuButtonStyle} onMouseEnter={e => e.target.style.background = 'var(--btn-hover)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                        ✏️ Edit Tournament
                    </button>

                    <button onClick={() => { copyTournamentLink(contextMenu.tournament?.link); setContextMenu({ ...contextMenu, visible: false }); }} style={menuButtonStyle} onMouseEnter={e => e.target.style.background = 'var(--btn-hover)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                        🔗 Copy Invite Link
                    </button>

                    <button onClick={() => { handleDeleteTournament(contextMenu.tournament); setContextMenu({ ...contextMenu, visible: false }); }} style={{...menuButtonStyle, color: '#ef4444', marginTop: '4px'}} onMouseEnter={e => e.target.style.background = 'var(--btn-hover)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
                        🗑️ Delete Tournament
                    </button>
                </div>
            )}

            {/* ✏️ EDIT TOURNAMENT MODAL */}
            {isEditModalOpen && editData && (
                <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000, backdropFilter: 'blur(10px)' }}>
                    <div className="glass-panel" style={{padding: '35px', width: '90%', maxWidth: '500px', }}>
                        <h2 style={{color: 'var(--text-primary)', marginTop: 0, marginBottom: '20px' }}>✏️ Edit Tournament</h2>
                        
                        <form onSubmit={handleUpdateTournament} style={{display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={labelStyle}>Tournament Title</label>
                                <input type="text" value={editData.title} onChange={e => setEditData({...editData, title: e.target.value})} required className="neu-input" />
                            </div>
                            
                            <div style={{display: 'flex', gap: '10px' }}>
                                <div style={{flex: 1 }}>
                                    <label style={labelStyle}>Format</label>
                                    <select value={editData.format} onChange={e => setEditData({...editData, format: e.target.value})} className="neu-input">
                                        <option style={{background: 'var(--bg-primary)'}}>Arena (Continuous)</option>
                                        <option style={{background: 'var(--bg-primary)'}}>Swiss (Rounds)</option>
                                    </select>
                                </div>
                                <div style={{flex: 1 }}>
                                    <label style={labelStyle}>Time Control</label>
                                    <select value={editData.timeControl} onChange={e => setEditData({...editData, timeControl: e.target.value})} className="neu-input">
                                        <option style={{background: 'var(--bg-primary)'}}>1+0 Bullet</option><option style={{background: 'var(--bg-primary)'}}>3+0 Blitz</option><option style={{background: 'var(--bg-primary)'}}>5+0 Blitz</option><option style={{background: 'var(--bg-primary)'}}>10+0 Rapid</option><option style={{background: 'var(--bg-primary)'}}>15+10 Rapid</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label style={labelStyle}>Date & Time</label>
                                <input type="datetime-local" value={editData.date} onChange={e => setEditData({...editData, date: e.target.value})} required className="neu-input" />
                            </div>

                            <div>
                                <label style={labelStyle}>Lichess Link</label>
                                <input type="text" value={editData.link} onChange={e => setEditData({...editData, link: e.target.value})} className="neu-input" />
                            </div>

                            <div style={{display: 'flex', gap: '15px', marginTop: '15px' }}>
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className="neu-btn" style={{flex: 1 }}>Cancel</button>
                                <button type="submit" className="neu-btn-primary" style={{flex: 1 }}>Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Common Styles ---
const labelStyle = { display: 'block', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '8px', fontWeight: '600' };
const badgeStyle = { background: 'rgba(255,255,255,0.05)', color: '#2563eb', padding: '6px 14px', borderRadius: '50px', fontSize: '11px', fontWeight: 'bold', border: '1px solid rgba(255,255,255,0.1)' };
const menuButtonStyle = { width: '100%', textAlign: 'left', padding: '10px 15px', background: 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', fontSize: '14px', transition: 'all 0.15s ease' };
